import { useState } from 'react';
import { seoArticles } from '@/mocks/seoArticles';

export default function SEOArticlesSection() {
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const toggleArticle = (id: number) => {
    if (expandedId === id) {
      setExpandedId(null);
    } else {
      setExpandedId(id);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <section className="py-16 md:py-24 bg-background-100">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="text-center mb-12 md:mb-16">
          <span className="inline-flex items-center px-4 py-1.5 rounded-full border border-accent-200 text-accent-700 text-xs font-medium mb-4 whitespace-nowrap">
            Blog SEO
          </span>
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-heading text-foreground-950 leading-tight mb-4">
            Articles &amp; ressources<br />Hipobuy Spreadsheet
          </h2>
          <p className="text-foreground-600 text-base max-w-xl mx-auto">
            Explorez nos guides détaillés pour maîtriser Hipobuy Spreadsheet et optimiser votre productivité au quotidien.
          </p>
        </div>

        {/* Articles Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {seoArticles.map((article) => (
            <article
              key={article.id}
              className="bg-background-50 rounded-2xl overflow-hidden border border-background-200 hover:shadow-md transition-shadow group cursor-pointer"
              onClick={() => toggleArticle(article.id)}
            >
              {/* Image */}
              <div className="aspect-[16/10] overflow-hidden">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>

              {/* Content */}
              <div className="p-5">
                <span className="text-accent-600 text-xs font-semibold tracking-wider uppercase mb-2 block">
                  Article SEO #{article.id}
                </span>
                <h3 className="font-heading text-base md:text-lg text-foreground-950 mb-2 leading-snug group-hover:text-primary-600 transition-colors">
                  {article.title}
                </h3>
                <p className="text-foreground-600 text-sm leading-relaxed mb-4 line-clamp-3">
                  {article.summary}
                </p>
                <div className="flex items-center gap-2 text-primary-600 text-sm font-medium">
                  <span>Lire l&apos;article</span>
                  <i className={`ri-arrow-down-s-line w-4 h-4 flex items-center justify-center transition-transform ${expandedId === article.id ? 'rotate-180' : ''}`}></i>
                </div>
              </div>

              {/* Expanded content */}
              {expandedId === article.id && (
                <div className="px-5 pb-6 border-t border-background-200 mt-2 pt-5">
                  <p className="text-foreground-700 text-sm leading-relaxed mb-4">
                    {article.content}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <a
                      href="https://kaspreadsheet.com/"
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Découvrir Hipobuy Spreadsheet
                      <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
                    </a>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedId(null);
                      }}
                      className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full border border-background-300 text-foreground-600 text-sm font-medium hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Fermer
                      <i className="ri-close-line w-4 h-4 flex items-center justify-center"></i>
                    </button>
                  </div>
                </div>
              )}
            </article>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-foreground-950 text-background-50 text-sm font-semibold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
          >
            Voir tous les guides Hipobuy Spreadsheet
            <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
          </a>
        </div>
      </div>

      {/* Structured data for multiple articles */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            seoArticles.map((article) => ({
              '@context': 'https://schema.org',
              '@type': 'Article',
              headline: article.title,
              description: article.summary,
              author: {
                '@type': 'Organization',
                name: 'Équipe Hipobuy',
              },
              datePublished: '2026-06-23',
              inLanguage: 'fr',
            }))
          ),
        }}
      />
    </section>
  );
}